import type { ReactNode } from 'react';
import { getSubstringMatchIndices, getSymbolDisplayMatchIndices } from '../../../utils/substringMatch';
import type { AutocompleteSearchResult } from '../../Form/Autocomplete/types';
import { ResultMatch, ResultMatchType } from '../../ResultMatch';
import { Description, TextWrapper, Title, Wrapper } from './styles';

/**
 * `searchString` is required because it is the only source of match highlighting: omitting it
 * renders the whole label as a match, silently losing the highlight of what the user typed.
 */
export type AutocompleteResultOptions = {
  /**
   * When false, omit the label's HTML `title`. SearchSelect turns this off for rows that already have a hover tooltip;
   * otherwise the browser title stacks on top of that tooltip. Defaults to true (truncated labels still get a native title).
   */
  nativeTitle?: boolean;
  /**
   * Align a hyphenless query to the label. "ethusd" highlights ETH and USD in ETH-USD instead of
   * the whole symbol when the match exists only on the hyphen-stripped search symbol.
   */
  matchAcrossHyphens?: boolean;
};

export const AutocompleteResult = <T,>(
  searchResult: AutocompleteSearchResult<T>,
  disabled: boolean,
  searchString: string,
  renderPrefix?: (item: T) => ReactNode,
  { nativeTitle = true, matchAcrossHyphens = false }: AutocompleteResultOptions = {}
) => {
  const labelIndices = matchAcrossHyphens
    ? getSymbolDisplayMatchIndices(searchResult.item.label, searchString.trim().split(/\s+/))
    : getSubstringMatchIndices(searchResult.item.label, searchString);
  const descriptionIndices = getSubstringMatchIndices(searchResult.item.description, searchString);

  const anyMatches = labelIndices.length > 0 || descriptionIndices.length > 0;

  return (
    <Wrapper>
      {renderPrefix?.(searchResult.item.item)}
      <TextWrapper>
        <Title
          disabled={disabled}
          data-testid="dropdown-result-label"
          title={nativeTitle ? searchResult.item.label : undefined}
        >
          <ResultMatch
            label={searchResult.item.label}
            indices={labelIndices}
            highlightAll={!anyMatches}
            type={ResultMatchType.Label}
            disabled={disabled}
          />
        </Title>
        {searchResult.item.description && (
          <Description disabled={disabled} data-testid="dropdown-result-description">
            <ResultMatch
              label={searchResult.item.description}
              indices={descriptionIndices}
              highlightAll={!anyMatches}
              type={ResultMatchType.Description}
              disabled={disabled}
            />
          </Description>
        )}
      </TextWrapper>
    </Wrapper>
  );
};
